import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users, Wifi, Clock, AlertTriangle, Activity,
  FileText, Timer, ShieldCheck, Inbox, Navigation, MapPin, Phone,
  Droplet, TrendingUp,
} from "lucide-react";

export default function OwnerDashboard() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [lateAlerts, setLateAlerts] = useState([]);
  const [atRiskJobs, setAtRiskJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, actRes, lateRes, riskRes] = await Promise.allSettled([
        API.get("/management/dashboard"),
        API.get("/management/activity?limit=10"),
        API.get("/management/late-alerts"),
        API.get("/floor-plans/intelligence/at-risk-jobs"),
      ]);
      if (dashRes.status === "fulfilled") setData(dashRes.value.data);
      if (actRes.status  === "fulfilled") setActivity(actRes.value.data.activities);
      if (lateRes.status === "fulfilled") setLateAlerts(lateRes.value.data.alerts);
      if (riskRes.status === "fulfilled") setAtRiskJobs(riskRes.value.data.jobs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  if (loading || !data) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const kpiCards = [
    { label: "Total Technicians", value: data.total_vendors, icon: Users, color: "text-zinc-600" },
    { label: "Online Now", value: data.online_vendors, icon: Wifi, color: "text-emerald-600" },
    { label: "Busy", value: data.busy_vendors, icon: Activity, color: "text-amber-600" },
    { label: "Pending Approval", value: data.pending_approvals, icon: ShieldCheck, color: "text-blue-600" },
    { label: "On Hold", value: data.on_hold_vendors, icon: AlertTriangle, color: "text-rose-600" },
    { label: "Active Requests", value: data.active_requests, icon: FileText, color: "text-zinc-600" },
    { label: "New Today", value: data.new_today, icon: Inbox, color: "text-blue-600" },
    { label: "Waiting Assignment", value: data.requests_waiting, icon: Clock, color: "text-amber-600" },
  ];

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900 tracking-tight">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Real-time operations overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        {kpiCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="border border-zinc-200 card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold truncate">{label}</span>
              </div>
              <p className="text-2xl font-heading font-bold text-zinc-900">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {atRiskJobs.length > 0 && (
        <Card className="border-2 border-orange-300" data-testid="at-risk-jobs-panel">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Droplet className="w-5 h-5 text-orange-600" />
              <h3 className="text-sm font-heading font-bold text-orange-700">
                At-Risk Drying Jobs ({atRiskJobs.length})
              </h3>
              <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px] ml-auto">
                NOT DRYING
              </Badge>
            </div>
            <p className="text-xs text-orange-700/80 mb-3">
              Floor plans where moisture pin readings are trending wetter than dry, or have ≥1 pin stuck for 3+ readings. Investigate before water damage becomes a claim problem.
            </p>
            <div className="space-y-2">
              {atRiskJobs.map(j => (
                <div key={j.request_id}
                     className="p-3 bg-white rounded-xl border border-orange-200"
                     data-testid={`at-risk-${j.request_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-400">{j.request_code}</span>
                        {j.stale_pin_count > 0 && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                            {j.stale_pin_count} stale pin{j.stale_pin_count > 1 ? "s" : ""}
                          </span>
                        )}
                        {j.drying_progress === "not_drying" && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200">
                            <TrendingUp className="w-2.5 h-2.5 inline -mt-0.5" /> Not drying
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-zinc-900 mt-0.5 truncate">
                        {j.service_type}
                      </p>
                      <p className="text-[11px] text-zinc-500 truncate">{j.address}</p>
                    </div>
                    <div className="text-right text-[10px] text-zinc-500 flex-shrink-0">
                      <p>{j.improving_pins}↓ · {j.worsening_pins}↑ · {j.flat_pins}—</p>
                      <p className="text-zinc-400">{j.vendor_name || "Unassigned"}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {lateAlerts.length > 0 && (
        <Card className="border-2 border-red-300 late-warning" data-testid="late-alerts-panel">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-sm font-heading font-bold text-red-700">
                Late Technician Alerts ({lateAlerts.length})
              </h3>
              <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] urgent-flash ml-auto">
                ATTENTION REQUIRED
              </Badge>
            </div>
            <div className="space-y-3">
              {lateAlerts.map((alert) => (
                <div
                  key={alert.request_id}
                  className="p-3 bg-white rounded-xl border border-red-200"
                  data-testid={`late-alert-${alert.request_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-400">{alert.request_code}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                          {alert.minutes_since_accepted}min late
                        </span>
                      </div>
                      <p className="text-sm font-medium text-zinc-900 mt-0.5">{alert.service_type}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      alert.current_status === "en_route"
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`}>
                      {alert.current_status === "en_route" ? "En Route" : "Accepted"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500 mb-2">
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{alert.vendor_name || "Unknown"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      <span>{alert.vendor_phone || "\u2014"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{alert.property_address}</span>
                    </div>
                    {alert.vendor_latitude && (
                      <div className="flex items-center gap-1">
                        <Navigation className="w-3 h-3 text-red-500" />
                        <span className="font-mono text-[10px]">
                          {alert.vendor_latitude.toFixed(4)}, {alert.vendor_longitude.toFixed(4)}
                        </span>
                      </div>
                    )}
                  </div>
                  {alert.late_reason ? (
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-[10px] text-amber-600 font-bold uppercase mb-0.5">Technician's Reason:</p>
                      <p className="text-xs text-amber-800">{alert.late_reason}</p>
                    </div>
                  ) : (
                    <p className="text-[10px] text-red-500 italic">No reason provided yet</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-zinc-200" data-testid="today-performance">
          <CardContent className="p-5">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Today's Performance</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-sm text-zinc-600">Accepted</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{data.accepted_today}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm text-zinc-600">Passed</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{data.passed_today}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-rose-500" />
                  <span className="text-sm text-zinc-600">Missed</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{data.missed_today}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-sm text-zinc-600">Completed</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">{data.completed_today}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
                <div className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-600">Avg Response Time</span>
                </div>
                <span className="text-sm font-bold text-zinc-900">
                  {data.avg_response_time > 0 ? `${Math.round(data.avg_response_time)}s` : "N/A"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200" data-testid="activity-feed">
          <CardContent className="p-5">
            <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Recent Activity</h3>
            {activity.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-4">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {activity.slice(0, 8).map(act => (
                  <div key={act.id} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-zinc-300 mt-1.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-zinc-700 truncate">{act.action_label}</p>
                      <p className="text-[10px] text-zinc-400">
                        {new Date(act.created_at).toLocaleString()} &middot; {act.actor_role}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-zinc-200" data-testid="technician-summary">
        <CardContent className="p-5">
          <h3 className="text-sm font-heading font-semibold text-zinc-900 mb-4">Technician Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-zinc-50 rounded-xl">
              <p className="text-2xl font-heading font-bold text-zinc-900">{data.approved_vendors}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">Approved</p>
            </div>
            <div className="text-center p-3 bg-emerald-50 rounded-xl">
              <p className="text-2xl font-heading font-bold text-emerald-700">{data.online_vendors}</p>
              <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-bold">Online</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-xl">
              <p className="text-2xl font-heading font-bold text-amber-700">{data.pending_approvals}</p>
              <p className="text-[10px] text-amber-600 uppercase tracking-wider font-bold">Pending</p>
            </div>
            <div className="text-center p-3 bg-rose-50 rounded-xl">
              <p className="text-2xl font-heading font-bold text-rose-700">{data.on_hold_vendors}</p>
              <p className="text-[10px] text-rose-600 uppercase tracking-wider font-bold">On Hold</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
